import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, fileUrl } from '../lib/api';
import { setPageMeta } from '../lib/seo';

// Campaign entrance at "/" (Phase 92, revision 2 per Steve).
// Steve's photo is the WHOLE background; the campaign logo sits over it on
// the left, matching the composition of the approved hero image. Text and
// button boxes are reversed: dark on light instead of white on black.
// The full information homepage lives at /home.
// SEO/accessibility: real title + meta description, semantic h1, alt text,
// and crawlable links to /home, /platform, and the PITA hub.
const ENTRANCE_DEFAULTS = {
  portrait_url: '/branding/steve-portrait.webp',
  tagline: 'PROMISE ACCOUNTABILITY BY BUILDING A SYSTEM FOR IT.',
  secondary_line: '',
};

export default function Entrance() {
  const [cfg, setCfg] = useState(ENTRANCE_DEFAULTS);

  useEffect(() => {
    api.get('/entrance-page').then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
    setPageMeta({
      title: 'Steve Lesperance for CKL Councillor Ward 8 | SteveLesperance.com',
      description: 'Elect Steve Lesperance for Ward 8 Councillor, City of Kawartha Lakes. Accountability you can see: the platform, the Public Issue Tracker App (PITA), and the full campaign site.',
      url: window.location.origin + '/',
    });
  }, []);

  const portrait = cfg.portrait_url ? (cfg.portrait_url.startsWith('/branding/') ? cfg.portrait_url : fileUrl(cfg.portrait_url)) : ENTRANCE_DEFAULTS.portrait_url;

  return (
    <div className="relative min-h-screen bg-white flex flex-col overflow-hidden" data-testid="entrance-page">
      <h1 className="sr-only">Steve Lesperance for CKL Councillor Ward 8, Kawartha Lakes. SteveLesperance.com</h1>

      {/* Full-bleed background photo (admin-swappable in Hero Manager) */}
      <img
        src={portrait}
        alt="Steve Lesperance, candidate for Ward 8 Councillor"
        className="entrance-bg"
        data-testid="entrance-portrait-image"
      />
      {/* Readability overlays: soft white fade on the left (desktop) and
          from the bottom (mobile) so the dark logo and text stay crisp */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/45 to-transparent hidden sm:block" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-white via-white/85 to-transparent sm:hidden" />

      {/* Identity block: logo over the photo, like the approved hero */}
      <main className="relative flex-1 flex items-end sm:items-center">
        <div className="w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-14">
          <div className="max-w-xl flex flex-col items-start pb-8 sm:pb-0">
            <img
              src="/branding/ElectSteveLesperance2.png"
              alt="Steve Lesperance for CKL Councillor Ward 8, SteveLesperance.com"
              className="w-full max-w-sm sm:max-w-md xl:max-w-lg h-auto"
              data-testid="entrance-logo-image"
            />

            <p className="mt-7 text-[#0b0f17] font-bold text-sm sm:text-base tracking-[0.28em]" style={{ fontFamily: 'Montserrat, sans-serif' }} data-testid="entrance-tagline">
              {cfg.tagline || ENTRANCE_DEFAULTS.tagline}
            </p>
            {cfg.secondary_line && (
              <p className="mt-2 text-[#24406f] text-xs sm:text-sm tracking-[0.2em]" data-testid="entrance-secondary-line">{cfg.secondary_line}</p>
            )}

            <nav className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto" aria-label="Campaign entrance">
              <Link to="/platform" className="entrance-btn" data-testid="entrance-platform-button">PLATFORM</Link>
              <Link to="/home" className="entrance-btn entrance-btn-primary" data-testid="entrance-enter-button">ENTER THE SITE</Link>
              <Link to="/issue-tracker" className="entrance-btn" data-testid="entrance-pita-button">PITA</Link>
            </nav>
          </div>
        </div>
      </main>

      <footer className="relative pb-5 px-4">
        <p className="text-center text-[#0b0f17]/45 text-[11px] tracking-wide font-semibold" data-testid="entrance-footer-line">
          Campaign to Elect Steve Lesperance, Ward 8, City of Kawartha Lakes. SteveLesperance.com
        </p>
      </footer>
    </div>
  );
}
